import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    limit,
    orderBy,
    query,
    updateDoc,
    where,
    Timestamp,
    DocumentReference
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { auth, db, storage } from '@/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { formatPhoneNumber } from '../../../utils/phone';
import {
    Calendar,
    ChevronDown,
    ChevronUp,
    ChevronLeft,
    ChevronRight,
    X,
    Trash2,
    Eye,
    Mail,
    Clock,
    FileText,
    Download
} from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────────────

interface UserData {
    id: string;
    [key: string]: any;
}

interface OrderSummary {
    id: string;
    status?: string;
    orderNumber?: string;
    createdAt?: any;
    totalAmount?: number;
    shikakuId?: DocumentReference | string;
    shikakuName?: string;
    trouble?: boolean;
}

interface MemoData {
    id: string;
    CreateAt?: Timestamp;
    text?: string;
    CreateUser?: DocumentReference | string;
    createUserName?: string;
    task?: 'active' | 'done' | null;
    tasklimit?: Timestamp | null;
    account?: DocumentReference | string | null;
    accountName?: string;
}

interface EmailData {
    id: string;
    subject?: string;
    body?: string;
    createdAt?: Timestamp;
    sendStatus?: string;
    direction?: string;
    status?: string;
    customerId?: string | null;
    customerEmail?: string;
}

interface FileData {
    id: string;
    title?: string;
    fileName?: string;
    storagePath?: string;
    uploadDate?: Timestamp;
    uploadedByName?: string;
}

interface ShikakuOption {
    id: string;
    ShikakuName: string;
}

interface Toast {
    id: number;
    message: string;
    type: 'success' | 'error';
}

// ─── Constants ───────────────────────────────────────────────────────────────

const BRAND = '#00b074';

const EDITABLE_FIELDS = [
    'display_name',
    'email',
    'phoneNumber',
    'Role',
    'postalCode',
    'address',
    'postalC',
    'AddressC',
    'sfId',
];


// ─── Helpers ─────────────────────────────────────────────────────────────────

const normalizeValue = (value: any, key?: string, seen: WeakSet<object> = new WeakSet()): any => {
    if (value === null || value === undefined) return value;
    if (value instanceof Timestamp) return value.toDate().toLocaleString('ja-JP');
    if (value instanceof DocumentReference) return value.path || value.id;
    if (typeof value === 'string' && key?.toLowerCase().includes('phone')) return formatPhoneNumber(value);
    if (typeof value === 'object') {
        if (seen.has(value)) return '[Circular]';
        seen.add(value);
        if (Array.isArray(value)) return value.map((item) => normalizeValue(item, undefined, seen));
        return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, normalizeValue(v, k, seen)]));
    }
    return value;
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function UserDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { userId: currentUserId } = useAuth();

    // Core data
    const [userData, setUserData] = useState<UserData | null>(null);
    const [editValues, setEditValues] = useState<Partial<UserData>>({});
    const [orders, setOrders] = useState<OrderSummary[]>([]);
    const [memos, setMemos] = useState<MemoData[]>([]);
    const [emails, setEmails] = useState<EmailData[]>([]);
    const [files, setFiles] = useState<FileData[]>([]);
    const [shikakuList, setShikakuList] = useState<ShikakuOption[]>([]);

    // UI state
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'memo' | 'email' | 'file'>('memo');
    const [accordion1Open, setAccordion1Open] = useState(false);
    const [toasts, setToasts] = useState<Toast[]>([]);

    // Order modal
    const [isAddingOrder, setIsAddingOrder] = useState(false);
    const [addingOrder, setAddingOrder] = useState(false);
    const [newOrderForm, setNewOrderForm] = useState({
        shikakuId: '',
        status: 'active',
        course: '通信講座＋試験',
        diploma: '通常版',
        paymentMethod: 'クレジット',
        totalAmount: 0,
        isExamFinished: false,
    });

    // Memo create modal
    const [isMemoModalOpen, setIsMemoModalOpen] = useState(false);
    const [memoText, setMemoText] = useState('');
    const [memoCreatedAt, setMemoCreatedAt] = useState<Date | null>(null);
    const [memoTaskStatus, setMemoTaskStatus] = useState<'active' | 'done' | null>(null);
    const [memoTaskLimit, setMemoTaskLimit] = useState('');
    const [memoAccountId, setMemoAccountId] = useState('');
    const [memoOrderId, setMemoOrderId] = useState('');
    const [adminUsers, setAdminUsers] = useState<{ id: string; displayName: string }[]>([]);
    const [currentStaffName, setCurrentStaffName] = useState('');
    const [addingMemo, setAddingMemo] = useState(false);

    // Memo detail modal
    const [selectedMemo, setSelectedMemo] = useState<MemoData | null>(null);
    const [isMemoDetailOpen, setIsMemoDetailOpen] = useState(false);
    const [isMemoDetailEditing, setIsMemoDetailEditing] = useState(false);
    const [memoDetailText, setMemoDetailText] = useState('');
    const [memoDetailTask, setMemoDetailTask] = useState<'active' | 'done' | null>(null);
    const [memoDetailTaskLimit, setMemoDetailTaskLimit] = useState('');
    const [memoDetailAccountId, setMemoDetailAccountId] = useState('');
    const [memoDetailSaving, setMemoDetailSaving] = useState(false);

    // Email modal
    const [selectedEmail, setSelectedEmail] = useState<EmailData | null>(null);

    // File upload modal
    const [isFileModalOpen, setIsFileModalOpen] = useState(false);
    const [fileTitle, setFileTitle] = useState('');
    const [selectedUploadFile, setSelectedUploadFile] = useState<File | null>(null);
    const [uploadingFile, setUploadingFile] = useState(false);

    // メール紐づけ
    const [linkingEmails, setLinkingEmails] = useState(false);
    const [linkResult, setLinkResult] = useState<string | null>(null);

    const handleLinkEmails = async () => {
        if (!userData || linkingEmails) return;
        const email = userData.email as string | undefined;
        if (!email) return;
        setLinkingEmails(true);
        setLinkResult(null);
        try {
            // customerEmail が一致して customerId が未設定のものを取得
            const q = query(
                collection(db, 'mail_messages'),
                where('customerEmail', '==', email)
            );
            const snap = await getDocs(q);
            const unlinked = snap.docs.filter((d) => !d.data().customerId);
            if (unlinked.length === 0) {
                setLinkResult('未紐づけのメールはありません');
                return;
            }
            const userRef = doc(db, 'User', userData.id);
            const displayName = (userData.display_name || userData.display_Name || null) as string | null;
            await Promise.all(
                unlinked.map((d) =>
                    updateDoc(d.ref, {
                        customerId: userData.id,
                        customerRef: userRef,
                        customerName: displayName,
                    })
                )
            );
            setLinkResult(`${unlinked.length}件を紐づけました`);
            // 一覧をリロード
            await loadEmails(email);
        } catch (err) {
            console.error('メール紐づけエラー:', err);
            setLinkResult('エラーが発生しました');
        } finally {
            setLinkingEmails(false);
            setTimeout(() => setLinkResult(null), 4000);
        }
    };

    // ── Toast helpers ──────────────────────────────────────────────────────

    const showToast = (message: string, type: 'success' | 'error' = 'success') => {
        const toastId = Date.now();
        setToasts((prev) => [...prev, { id: toastId, message, type }]);
        setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== toastId)), 3000);
    };

    // ── Firebase helpers ───────────────────────────────────────────────────

    const resolveShikakuName = async (shikakuValue?: DocumentReference | string) => {
        if (!shikakuValue) return '-';
        try {
            const ref = typeof shikakuValue === 'string' ? doc(db, 'Shikaku', shikakuValue) : shikakuValue;
            const snap = await getDoc(ref);
            return snap.exists() ? snap.data().ShikakuName || snap.id : '-';
        } catch {
            return '-';
        }
    };

    const loadOrdersByUser = async (userId: string) => {
        const userRef = doc(db, 'User', userId);
        const q = query(collection(db, 'Order'), where('UserRef', '==', userRef));
        const snap = await getDocs(q);
        const orderData = await Promise.all(
            snap.docs.map(async (d) => {
                const data = d.data() as OrderSummary;
                const shikakuName = await resolveShikakuName(data.shikakuId);
                return { id: d.id, ...data, shikakuName } as OrderSummary;
            })
        );
        setOrders(orderData);
    };

    const loadMemos = async (userId: string) => {
        try {
            const userRef = doc(db, 'User', userId);
            const q = query(
                collection(db, 'Memo'),
                where('TGTuser', '==', userRef),
                orderBy('CreateAt', 'desc')
            );
            const snap = await getDocs(q);
            const memoData = await Promise.all(
                snap.docs.map(async (d) => {
                    const data = d.data();
                    let createUserName = '';
                    if (data.CreateUser) {
                        try {
                            const ref = typeof data.CreateUser === 'string'
                                ? doc(db, 'User', data.CreateUser)
                                : data.CreateUser as DocumentReference;
                            const userSnap = await getDoc(ref);
                            createUserName = userSnap.exists() ? userSnap.data().display_name || userSnap.data().display_Name || '' : '';
                        } catch { /* ignore */ }
                    }
                    let accountName = '';
                    if (data.account) {
                        try {
                            const ref = typeof data.account === 'string'
                                ? doc(db, 'User', data.account)
                                : data.account as DocumentReference;
                            const accSnap = await getDoc(ref);
                            accountName = accSnap.exists() ? accSnap.data().display_name || accSnap.data().display_Name || '' : '';
                        } catch { /* ignore */ }
                    }
                    return { id: d.id, ...data, createUserName, accountName } as MemoData;
                })
            );
            setMemos(memoData);
        } catch (err) {
            console.error('Error loading memos:', err);
        }
    };

    const loadEmails = async (email: string) => {
        try {
            const msgQ = query(
                collection(db, 'mail_messages'),
                where('customerEmail', '==', email),
                orderBy('createdAt', 'desc')
            );
            const msgSnap = await getDocs(msgQ);
            const allEmails = msgSnap.docs.map((d) => ({ id: d.id, ...d.data() } as EmailData));
            setEmails(allEmails);
        } catch (err) {
            console.error('Error loading emails:', err);
        }
    };

    const loadFiles = async (userId: string) => {
        try {
            const userRef = doc(db, 'User', userId);
            const q = query(
                collection(db, 'UserFiles'),
                where('TGTuser', '==', userRef),
                orderBy('uploadDate', 'desc')
            );
            const snap = await getDocs(q);
            setFiles(snap.docs.map((d) => ({ id: d.id, ...d.data() } as FileData)));
        } catch (err) {
            console.error('Error loading files:', err);
        }
    };

    const fetchShikaku = async () => {
        try {
            const snap = await getDocs(collection(db, 'Shikaku'));
            setShikakuList(snap.docs.map((d) => ({ id: d.id, ShikakuName: d.data().ShikakuName || d.id })));
        } catch (err) {
            console.error('Error fetching shikaku:', err);
        }
    };

    // ── Order helpers ──────────────────────────────────────────────────────

    const generateOrderNumber = () => {
        const now = new Date();
        const y = now.getFullYear();
        const m = String(now.getMonth() + 1).padStart(2, '0');
        const d2 = String(now.getDate()).padStart(2, '0');
        const rand = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
        return `ORD-${y}${m}${d2}-${rand}`;
    };

    const resetOrderForm = () =>
        setNewOrderForm({ shikakuId: '', status: 'active', course: '通信講座＋試験', diploma: '通常版', paymentMethod: 'クレジット', totalAmount: 0, isExamFinished: false });

    const handleAddOrder = async () => {
        if (!id || addingOrder) return;
        if (!newOrderForm.shikakuId) { showToast('資格を選択してください', 'error'); return; }
        setAddingOrder(true);
        try {
            const createdAt = Timestamp.now();
            const expiresAt = Timestamp.fromDate(new Date(createdAt.toDate().getTime() + 2 * 365 * 24 * 60 * 60 * 1000));
            const shikakuRef = doc(db, 'Shikaku', newOrderForm.shikakuId);
            let examSetRef: any = null;

            const q1 = query(collection(db, 'ExamSets'), where('shikakuRef', '==', shikakuRef), where('effectiveFrom', '<=', createdAt), orderBy('effectiveFrom', 'desc'), limit(1));
            const s1 = await getDocs(q1);
            if (!s1.empty) {
                examSetRef = s1.docs[0].ref;
            } else {
                const q2 = query(collection(db, 'ExamSets'), where('shikakuRef', '==', shikakuRef), where('version', '==', 0), limit(1));
                const s2 = await getDocs(q2);
                if (!s2.empty) examSetRef = s2.docs[0].ref;
            }

            await addDoc(collection(db, 'Order'), {
                UserRef: doc(db, 'User', id),
                shikakuId: shikakuRef,
                examSetRef,
                orderNumber: generateOrderNumber(),
                createdAt,
                expiresAt,
                status: newOrderForm.status,
                course: newOrderForm.course,
                diploma: newOrderForm.diploma,
                paymentMethod: newOrderForm.paymentMethod,
                totalAmount: Number(newOrderForm.totalAmount) || 0,
                isExamFinished: newOrderForm.isExamFinished,
                retest1: false,
                retest2: false,
                trouble: false,
                lost: false,
            });

            showToast('Orderを追加しました');
            setIsAddingOrder(false);
            resetOrderForm();
            await loadOrdersByUser(id);
        } catch (err: any) {
            showToast(err?.message || 'Order追加に失敗しました', 'error');
        } finally {
            setAddingOrder(false);
        }
    };

    // ── Memo helpers ───────────────────────────────────────────────────────

    const formatMemoDate = (value?: Date | null) => {
        if (!value) return '-';
        return value.toLocaleString('ja-JP');
    };

    const handleOpenMemoModal = () => {
        setMemoCreatedAt(new Date());
        setMemoText('');
        setMemoTaskStatus(null);
        setMemoTaskLimit('');
        setMemoAccountId('');
        setMemoOrderId(orders.length === 1 ? orders[0].id : '');
        setIsMemoModalOpen(true);
    };

    const handleCreateMemo = async () => {
        if (!id || !memoText.trim() || addingMemo) return;
        if (memoTaskStatus === 'active') {
            if (!memoTaskLimit) { showToast('タスク期日を入力してください', 'error'); return; }
            if (!memoAccountId) { showToast('担当者を選択してください', 'error'); return; }
        }
        setAddingMemo(true);
        try {
            const createdAtValue = memoCreatedAt ?? new Date();
            const taskLimitDate = memoTaskLimit ? new Date(`${memoTaskLimit}T00:00:00`) : null;
            const shouldIncludeTaskDetails = memoTaskStatus === 'active' || memoTaskStatus === 'done';
            await addDoc(collection(db, 'Memo'), {
                CreateAt: Timestamp.fromDate(createdAtValue),
                text: memoText.trim(),
                CreateUser: currentUserId ? doc(db, 'User', currentUserId) : null,
                TGTuser: doc(db, 'User', id),
                TGTorder: memoOrderId ? doc(db, 'Order', memoOrderId) : null,
                task: memoTaskStatus ?? null,
                tasklimit: shouldIncludeTaskDetails && taskLimitDate ? Timestamp.fromDate(taskLimitDate) : null,
                account: shouldIncludeTaskDetails && memoAccountId ? doc(db, 'User', memoAccountId) : null,
            });
            setIsMemoModalOpen(false);
            showToast('メモを追加しました');
            await loadMemos(id);
        } catch (err: any) {
            showToast(err?.message || 'メモの追加に失敗しました', 'error');
        } finally {
            setAddingMemo(false);
        }
    };

    const handleDeleteMemo = async (memoId: string) => {
        if (!window.confirm('このメモを削除しますか？')) return;
        try {
            await deleteDoc(doc(db, 'Memo', memoId));
            showToast('メモを削除しました');
            setMemos((prev) => prev.filter((m) => m.id !== memoId));
        } catch (err: any) {
            showToast(err?.message || 'メモの削除に失敗しました', 'error');
        }
    };

    const canDeleteMemo = (memo: MemoData) => {
        if (!currentUserId) return false;
        const creator = memo.CreateUser;
        const creatorId = typeof creator === 'string' ? creator : (creator as DocumentReference)?.id;
        return creatorId === currentUserId;
    };

    const handleOpenMemoDetail = (memo: MemoData) => {
        setSelectedMemo(memo);
        setMemoDetailText(memo.text ?? '');
        setMemoDetailTask(memo.task ?? null);
        setMemoDetailTaskLimit(
            memo.tasklimit instanceof Timestamp
                ? memo.tasklimit.toDate().toISOString().slice(0, 10)
                : ''
        );
        // find account id from adminUsers by name, or extract from ref
        const accRef = memo.account;
        const accId = accRef
            ? typeof accRef === 'string'
                ? accRef
                : (accRef as DocumentReference).id
            : '';
        setMemoDetailAccountId(accId);
        setIsMemoDetailEditing(false);
        setIsMemoDetailOpen(true);
    };

    const handleUpdateMemoDetail = async () => {
        if (!selectedMemo || memoDetailSaving) return;
        const trimmedText = memoDetailText.trim();
        if (!trimmedText) { showToast('メモ内容を入力してください', 'error'); return; }
        if (memoDetailTask === 'active') {
            if (!memoDetailTaskLimit) { showToast('タスク期日を入力してください', 'error'); return; }
            if (!memoDetailAccountId) { showToast('担当者を選択してください', 'error'); return; }
        }
        setMemoDetailSaving(true);
        try {
            const shouldIncludeTask = memoDetailTask === 'active' || memoDetailTask === 'done';
            const taskLimitDate = memoDetailTaskLimit ? new Date(`${memoDetailTaskLimit}T00:00:00`) : null;
            const updates: Record<string, any> = {
                text: trimmedText,
                task: memoDetailTask ?? null,
                tasklimit: shouldIncludeTask && taskLimitDate ? Timestamp.fromDate(taskLimitDate) : null,
                account: shouldIncludeTask && memoDetailAccountId ? doc(db, 'User', memoDetailAccountId) : null,
            };
            await updateDoc(doc(db, 'Memo', selectedMemo.id), updates);
            const accountName = adminUsers.find((u) => u.id === memoDetailAccountId)?.displayName ?? selectedMemo.accountName ?? '';
            setMemos((prev) => prev.map((m) =>
                m.id === selectedMemo.id
                    ? { ...m, text: trimmedText, task: memoDetailTask ?? null,
                        tasklimit: shouldIncludeTask && taskLimitDate ? Timestamp.fromDate(taskLimitDate) : null,
                        accountName: shouldIncludeTask ? accountName : '' }
                    : m
            ));
            setSelectedMemo((prev) => prev ? { ...prev, text: trimmedText, task: memoDetailTask ?? null, accountName: shouldIncludeTask ? accountName : '' } : prev);
            setIsMemoDetailEditing(false);
            showToast('メモを更新しました');
        } catch (err: any) {
            showToast(err?.message || 'メモの更新に失敗しました', 'error');
        } finally {
            setMemoDetailSaving(false);
        }
    };

    const handleDeleteMemoDetail = async () => {
        if (!selectedMemo || memoDetailSaving) return;
        if (!canDeleteMemo(selectedMemo)) { showToast('作成者本人のみ削除できます', 'error'); return; }
        if (!window.confirm('このメモを削除しますか？')) return;
        setMemoDetailSaving(true);
        try {
            await deleteDoc(doc(db, 'Memo', selectedMemo.id));
            setMemos((prev) => prev.filter((m) => m.id !== selectedMemo.id));
            setIsMemoDetailOpen(false);
            setSelectedMemo(null);
            showToast('メモを削除しました');
        } catch (err: any) {
            showToast(err?.message || 'メモの削除に失敗しました', 'error');
        } finally {
            setMemoDetailSaving(false);
        }
    };

    // ── File helpers ───────────────────────────────────────────────────────

    const handleOpenFileModal = () => {
        setFileTitle('');
        setSelectedUploadFile(null);
        setIsFileModalOpen(true);
    };

    const handleUploadFile = async () => {
        if (!id || uploadingFile) return;
        if (!fileTitle.trim()) { showToast('タイトルを入力してください', 'error'); return; }
        if (!selectedUploadFile) { showToast('ファイルを選択してください', 'error'); return; }
        setUploadingFile(true);
        try {
            const docRef = await addDoc(collection(db, 'UserFiles'), {
                title: fileTitle.trim(),
                fileName: selectedUploadFile.name,
                storagePath: '',
                uploadDate: Timestamp.now(),
                uploadedByName: currentStaffName || auth.currentUser?.email || '',
                TGTuser: doc(db, 'User', id),
            });
            const storagePath = `admin/user-files/${id}/${docRef.id}/${selectedUploadFile.name}`;
            await uploadBytes(ref(storage, storagePath), selectedUploadFile);
            await updateDoc(docRef, { storagePath });
            setIsFileModalOpen(false);
            showToast('ファイルをアップロードしました');
            await loadFiles(id);
        } catch (err: any) {
            showToast(err?.message || 'ファイルのアップロードに失敗しました', 'error');
        } finally {
            setUploadingFile(false);
        }
    };

    const handleDeleteFile = async (file: FileData) => {
        if (!id || !window.confirm('このファイルを削除しますか？')) return;
        try {
            await deleteDoc(doc(db, 'UserFiles', file.id));
            if (file.storagePath) await deleteObject(ref(storage, file.storagePath));
            setFiles((prev) => prev.filter((f) => f.id !== file.id));
            showToast('ファイルを削除しました');
        } catch (err: any) {
            showToast(err?.message || 'ファイルの削除に失敗しました', 'error');
        }
    };

    const handleDownloadFile = async (file: FileData) => {
        if (!file.storagePath) return;
        try {
            const url = await getDownloadURL(ref(storage, file.storagePath));
            window.open(url, '_blank');
        } catch (err: any) {
            showToast(err?.message || 'ファイルの取得に失敗しました', 'error');
        }
    };

    // ── Postal code lookup ────────────────────────────────────────────────

    const lookupPostalCode = async (postal: string, addressKey: 'address' | 'AddressC') => {
        const digits = postal.replace(/[^0-9]/g, '');
        if (digits.length !== 7) return;
        try {
            const res = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${digits}`);
            const json = await res.json();
            if (json.results && json.results.length > 0) {
                const { address1, address2, address3 } = json.results[0];
                setEdit(addressKey, `${address1}${address2}${address3}`);
            }
        } catch {
            // ignore network errors
        }
    };

    // ── User save ──────────────────────────────────────────────────────────

    const handleSave = async () => {
        if (!id || saving) return;
        setSaving(true);
        try {
            const updates: Record<string, any> = {};
            EDITABLE_FIELDS.forEach((f) => { if (editValues[f] !== undefined) updates[f] = editValues[f]; });
            await updateDoc(doc(db, 'User', id), updates);
            setUserData((prev) => (prev ? { ...prev, ...updates } : prev));
            setIsEditMode(false);
            showToast('ユーザー情報を更新しました');
        } catch (err: any) {
            showToast(err?.message || 'ユーザー情報の更新に失敗しました', 'error');
        } finally {
            setSaving(false);
        }
    };

    // ── Slider ────────────────────────────────────────────────────────────
    const [sliderPage, setSliderPage] = useState(0);
    const CARDS_PER_PAGE = 4;
    const sliderTotalPages = Math.ceil(orders.length / CARDS_PER_PAGE);
    const sliderOrders = orders.slice(sliderPage * CARDS_PER_PAGE, (sliderPage + 1) * CARDS_PER_PAGE);

    // ── Effects ────────────────────────────────────────────────────────────

    useEffect(() => {
        const fetchUser = async () => {
            if (!id) { setError('ユーザーIDが指定されていません'); setLoading(false); return; }
            try {
                const snap = await getDoc(doc(db, 'User', id));
                if (!snap.exists()) { setError('ユーザーが見つかりませんでした'); setLoading(false); return; }
                const data = { id: snap.id, ...snap.data() } as UserData;
                setUserData(data);
                setEditValues(data);
                await Promise.all([loadOrdersByUser(snap.id), loadMemos(snap.id), loadFiles(snap.id)]);
                if (data.email) await loadEmails(data.email);
            } catch (err: any) {
                setError(err?.message || 'ユーザー情報の取得に失敗しました');
            } finally {
                setLoading(false);
            }
        };
        fetchUser();
        fetchShikaku();
    }, [id]);

    useEffect(() => {
        const fetchAdminUsers = async () => {
            try {
                const snap = await getDocs(query(collection(db, 'User'), where('Role', '==', 'admin')));
                const options = snap.docs.map((d) => ({
                    id: d.id,
                    displayName: d.data().display_name || d.data().display_Name || d.id,
                }));
                options.sort((a, b) => a.displayName.localeCompare(b.displayName, 'ja'));
                setAdminUsers(options);
            } catch (err) {
                console.error('Error fetching admin users:', err);
            }
        };
        const fetchCurrentStaff = async () => {
            if (!currentUserId) return;
            try {
                const snap = await getDoc(doc(db, 'User', currentUserId));
                if (snap.exists()) setCurrentStaffName(snap.data().display_name || snap.data().display_Name || '');
            } catch { /* ignore */ }
        };
        fetchAdminUsers();
        fetchCurrentStaff();
    }, [currentUserId]);

    const normalizedUser = useMemo(() => userData ? normalizeValue(userData) : null, [userData]);

    // ── Derived helpers ────────────────────────────────────────────────────

    const displayVal = (key: string, fallbackKey?: string) => {
        const resolvedKey = userData && fallbackKey && !(key in userData) && fallbackKey in userData ? fallbackKey : key;
        return normalizedUser?.[resolvedKey] ?? '';
    };

    const editVal = (key: string, fallbackKey?: string) => {
        const resolvedKey = userData && fallbackKey && !(key in userData) && fallbackKey in userData ? fallbackKey : key;
        return editValues[resolvedKey] ?? '';
    };

    const setEdit = (key: string, value: any) => setEditValues((prev) => ({ ...prev, [key]: value }));

    const inputCls = 'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent';
    const inputFocus = `focus:ring-[${BRAND}]`;

    const roleColor = (role: string) => {
        if (role === 'admin') return 'bg-purple-100 text-purple-700';
        if (role === 'manager') return 'bg-blue-100 text-blue-700';
        return 'bg-slate-100 text-slate-600';
    };

    // ── Loading / Error screens ────────────────────────────────────────────

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-[#f9fbfc]">
            <div className="text-slate-500 text-sm">読み込み中...</div>
        </div>
    );

    if (error) return (
        <div className="min-h-screen flex items-center justify-center bg-[#f9fbfc]">
            <div className="bg-red-50 text-red-600 px-5 py-4 rounded-xl border border-red-200">{error}</div>
        </div>
    );

    const role = userData?.Role || '';
    const createdAtDisplay = userData?.createdAt instanceof Timestamp
        ? userData.createdAt.toDate().toLocaleString('ja-JP')
        : (normalizedUser?.createdAt ?? '');

    // ─────────────────────────────────────────────────────────────────────────

    return (
        <div className="min-h-screen bg-[#f9fbfc] px-6 py-6 max-w-6xl mx-auto space-y-5">

            {/* ── Toast ─────────────────────────────────────────────────────── */}
            <div className="fixed top-5 right-5 z-50 flex flex-col gap-2">
                {toasts.map((t) => (
                    <div key={t.id} className={`px-4 py-3 rounded-lg shadow-lg text-sm text-white transition-all ${t.type === 'error' ? 'bg-rose-500' : 'bg-[#00b074]'}`}>
                        {t.message}
                    </div>
                ))}
            </div>

            {/* ── Top bar ────────────────────────────────────────────────────── */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#00b074] text-white">顧客詳細</span>
                    {role && (
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${roleColor(role)}`}>{role}</span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigate(-1)}
                        className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition"
                    >
                        戻る
                    </button>
                    <button
                        onClick={() => {
                            if (isEditMode) {
                                setEditValues(userData ?? {});
                                setIsEditMode(false);
                            } else {
                                setIsEditMode(true);
                            }
                        }}
                        className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition"
                    >
                        {isEditMode ? '閲覧モードに戻る' : '編集する'}
                    </button>
                    {isEditMode && (
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="px-4 py-2 text-sm font-semibold text-white rounded-lg hover:opacity-90 transition disabled:opacity-60"
                            style={{ backgroundColor: BRAND }}
                        >
                            {saving ? '保存中...' : '変更を保存'}
                        </button>
                    )}
                </div>
            </div>

            {/* ── Profile header card ────────────────────────────────────────── */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-5">
                {/* Row 1 */}
                <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-wrap items-center gap-3">
                        {isEditMode
                            ? <input
                                value={editVal('display_name') as string}
                                onChange={(e) => setEdit('display_name', e.target.value)}
                                className={`${inputCls} ${inputFocus} text-xl font-bold w-48`}
                              />
                            : <span className="text-2xl font-bold text-slate-800">{displayVal('display_name') || '未設定'}</span>}
                        <span className="font-mono text-xs text-slate-400 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded">{id}</span>
                        {createdAtDisplay && (
                            <span className="flex items-center gap-1.5 text-xs text-slate-400">
                                <Calendar size={13} />
                                <span className="text-slate-500">作成日時</span>
                                <span>{createdAtDisplay}</span>
                            </span>
                        )}
                    </div>
                </div>

                {/* Row 2: contact info */}
                <div className="flex flex-wrap gap-x-8 gap-y-3">
                    {/* 電話番号 */}
                    <div className="shrink-0">
                        <div className="text-xs font-medium text-slate-400 mb-1">電話番号</div>
                        {isEditMode
                            ? <input value={editVal('phoneNumber') as string} onChange={(e) => setEdit('phoneNumber', e.target.value)} className={`${inputCls} ${inputFocus} w-40`} placeholder="電話番号" />
                            : <div className="text-sm text-slate-700 whitespace-nowrap">{formatPhoneNumber(String(displayVal('phoneNumber') || '')) || '未設定'}</div>}
                    </div>
                    {/* メールアドレス */}
                    <div className="shrink-0">
                        <div className="text-xs font-medium text-slate-400 mb-1">メールアドレス</div>
                        {isEditMode
                            ? <input value={editVal('email') as string} onChange={(e) => setEdit('email', e.target.value)} className={`${inputCls} ${inputFocus} w-56`} placeholder="メール" />
                            : <div className="text-sm text-slate-700">{displayVal('email') || '未設定'}</div>}
                    </div>
                    {/* 住所 */}
                    <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-slate-400 mb-1">住所</div>
                        {isEditMode
                            ? <div className="space-y-1">
                                <input value={editVal('postalCode') as string} onChange={(e) => { setEdit('postalCode', e.target.value); lookupPostalCode(e.target.value, 'address'); }} className={`${inputCls} ${inputFocus}`} placeholder="郵便番号（例：1234567）" />
                                <input value={editVal('address') as string} onChange={(e) => setEdit('address', e.target.value)} className={`${inputCls} ${inputFocus}`} placeholder="住所" />
                              </div>
                            : <div className="text-sm text-slate-700 leading-relaxed">
                                {displayVal('postalCode') && <div>〒{displayVal('postalCode')}</div>}
                                <div>{displayVal('address') || (!displayVal('postalCode') ? '未設定' : '')}</div>
                              </div>}
                    </div>
                </div>

                {/* Accordion: その他情報（補足住所含む） */}
                <div className="border border-slate-100 rounded-xl overflow-hidden">
                    <button
                        onClick={() => setAccordion1Open((v) => !v)}
                        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-slate-600 hover:bg-slate-50 transition"
                    >
                        その他情報
                        {accordion1Open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                    {accordion1Open && (
                        <div className="px-4 pb-4 pt-2 border-t border-slate-100 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <div className="text-xs font-medium text-slate-400 mb-1">ユーザーID</div>
                                    <div className="font-mono text-xs text-slate-500 bg-slate-50 border border-slate-200 px-2 py-1 rounded">{id}</div>
                                </div>
                                <div>
                                    <div className="text-xs font-medium text-slate-400 mb-1">SFID</div>
                                    {isEditMode
                                        ? <input
                                            value={editVal('sfId', 'sfid') as string}
                                            onChange={(e) => setEdit('sfId', e.target.value)}
                                            className={`${inputCls} ${inputFocus}`}
                                            placeholder="SFID"
                                          />
                                        : <div className="text-sm text-slate-700">{displayVal('sfId', 'sfid') || '未設定'}</div>}
                                </div>
                                <div>
                                    <div className="text-xs font-medium text-slate-400 mb-1">権限</div>
                                    {isEditMode
                                        ? <select
                                            value={editVal('Role') as string}
                                            onChange={(e) => setEdit('Role', e.target.value)}
                                            className={`${inputCls} ${inputFocus}`}
                                          >
                                            <option value="user">user</option>
                                            <option value="admin">admin</option>
                                            <option value="manager">manager</option>
                                          </select>
                                        : <div className="text-sm text-slate-700">{displayVal('Role') || '未設定'}</div>}
                                </div>
                            </div>
                            <div className="border-t border-slate-100 pt-4">
                                <div className="text-xs font-medium text-slate-400 mb-3">補足住所（請求先）</div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <div className="text-xs text-slate-400 mb-1">郵便番号</div>
                                        {isEditMode
                                            ? <input value={editVal('postalC') as string} onChange={(e) => { setEdit('postalC', e.target.value); lookupPostalCode(e.target.value, 'AddressC'); }} className={`${inputCls} ${inputFocus}`} placeholder="郵便番号（例：1234567）" />
                                            : <div className="text-sm text-slate-700">{displayVal('postalC') || '未設定'}</div>}
                                    </div>
                                    <div>
                                        <div className="text-xs text-slate-400 mb-1">住所</div>
                                        {isEditMode
                                            ? <input value={editVal('AddressC') as string} onChange={(e) => setEdit('AddressC', e.target.value)} className={`${inputCls} ${inputFocus}`} placeholder="住所" />
                                            : <div className="text-sm text-slate-700">{displayVal('AddressC') || '未設定'}</div>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Orders section ─────────────────────────────────────────────── */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2">
                        <h2 className="text-base font-bold text-slate-800">関連する注文</h2>
                        {orders.some((o) => o.trouble === true) && (
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-600">⚠ 要注意あり</span>
                        )}
                    </div>
                    <button
                        onClick={() => setIsAddingOrder(true)}
                        className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg hover:opacity-90 transition"
                        style={{ backgroundColor: BRAND }}
                    >
                        ＋ Order追加
                    </button>
                </div>

                {orders.length === 0 ? (
                    <div className="text-sm text-slate-400">関連する注文はありません</div>
                ) : (() => {
                    const useSlider = orders.length > 4;
                    const displayOrders = useSlider ? sliderOrders : orders;
                    const orderCards = displayOrders.map((order) => {
                        const isCancelled = order.status === 'cancel' || order.status === 'cancelled' || order.status === 'cancell';
                        const isTrouble = order.trouble === true;
                        const accentColor = isTrouble ? '#ef4444' : isCancelled ? '#9ca3af' : BRAND;
                        const createdStr = order.createdAt instanceof Timestamp
                            ? order.createdAt.toDate().toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' })
                            : normalizeValue(order.createdAt) ?? '';
                        return (
                            <div
                                key={order.id}
                                onClick={() => navigate(`/admin/orders/${order.id}`)}
                                className="rounded-xl border-2 overflow-hidden cursor-pointer hover:shadow-md transition"
                                style={{ borderColor: accentColor }}
                            >
                                <div className="px-3 pt-3 pb-2 flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span
                                            className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded border"
                                            style={{ borderColor: accentColor, color: accentColor }}
                                        >
                                            {order.status || '-'}
                                        </span>
                                        <span className="text-xs text-slate-800 truncate">{createdStr}</span>
                                    </div>
                                    <span className="text-xs font-bold text-slate-800 shrink-0">{order.orderNumber || ''}</span>
                                </div>
                                <div className="text-white text-base font-semibold text-center py-1.5 px-2 truncate" style={{ backgroundColor: accentColor }}>
                                    {order.shikakuName || '資格名未設定'}
                                </div>
                                <div className="text-center py-3 text-base font-bold text-slate-700">
                                    {(order.totalAmount ?? 0).toLocaleString()}円
                                </div>
                            </div>
                        );
                    });
                    return useSlider ? (
                        <div>
                            <div className="grid grid-cols-4 gap-4">
                                {orderCards}
                            </div>
                            <div className="flex items-center justify-center gap-3 mt-4">
                                <button
                                    onClick={() => setSliderPage((p) => Math.max(0, p - 1))}
                                    disabled={sliderPage === 0}
                                    className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-30 transition"
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                <span className="text-xs text-slate-500">{sliderPage + 1} / {sliderTotalPages}</span>
                                <button
                                    onClick={() => setSliderPage((p) => Math.min(sliderTotalPages - 1, p + 1))}
                                    disabled={sliderPage === sliderTotalPages - 1}
                                    className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-30 transition"
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {orderCards}
                        </div>
                    );
                })()}
            </div>

            {/* ── Notes + Email tabs ─────────────────────────────────────────── */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
                {/* Tab header */}
                <div className="flex items-center justify-between border-b border-slate-100 pr-4">
                    <div className="flex">
                        {(['memo', 'email', 'file'] as const).map((tab) => {
                            const count = tab === 'memo' ? memos.length : tab === 'email' ? emails.length : files.length;
                            const active = activeTab === tab;
                            return (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-5 py-3 text-sm font-medium flex items-center gap-1.5 border-b-2 transition ${active ? 'border-[#00b074] text-[#00b074]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                                >
                                    {tab === 'memo' ? <Clock size={14} /> : tab === 'email' ? <Mail size={14} /> : <FileText size={14} />}
                                    {tab === 'memo' ? 'メモ一覧' : tab === 'email' ? 'メール一覧' : 'ファイル一覧'}
                                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${active ? 'bg-[#00b074] text-white' : 'bg-slate-100 text-slate-500'}`}>
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    {activeTab === 'memo' && (
                        <button
                            onClick={handleOpenMemoModal}
                            className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg hover:opacity-90 transition"
                            style={{ backgroundColor: BRAND }}
                        >
                            ＋ メモ追加
                        </button>
                    )}
                    {activeTab === 'file' && (
                        <button
                            onClick={handleOpenFileModal}
                            className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg hover:opacity-90 transition"
                            style={{ backgroundColor: BRAND }}
                        >
                            ＋ ファイル追加
                        </button>
                    )}
                    {activeTab === 'email' && (
                        <div className="flex items-center gap-2">
                            {linkResult && (
                                <span className="text-xs font-semibold px-2 py-1 rounded-full bg-green-100 text-green-700">
                                    {linkResult}
                                </span>
                            )}
                            <button
                                onClick={handleLinkEmails}
                                disabled={linkingEmails}
                                className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition"
                            >
                                {linkingEmails ? '紐づけ中...' : '🔗 メール紐づけ'}
                            </button>
                        </div>
                    )}
                </div>

                <div className="p-6">
                    {/* ── Memo tab ── */}
                    {activeTab === 'memo' && (
                        <div className="space-y-5">
                            {/* Timeline */}
                            <div className="relative pl-6 space-y-4">
                                <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-slate-100" />
                                {memos.length === 0 && <div className="text-sm text-slate-400">メモはありません</div>}
                                {memos.map((memo) => {
                                    const dateStr = memo.CreateAt instanceof Timestamp
                                        ? memo.CreateAt.toDate().toLocaleString('ja-JP')
                                        : '';
                                    return (
                                        <div key={memo.id} className="relative">
                                            <div className="absolute -left-4 top-3 w-3 h-3 rounded-full bg-[#00b074] border-2 border-white shadow-sm" />
                                            <div
                                                className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm cursor-pointer hover:border-slate-300 transition"
                                                onClick={() => handleOpenMemoDetail(memo)}
                                            >
                                                <div className="flex items-center gap-2 flex-wrap mb-2">
                                                    <span className="text-xs text-slate-400">{dateStr}</span>
                                                    <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded">{memo.createUserName || '不明'}</span>
                                                    {memo.task && (
                                                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${memo.task === 'done' ? 'bg-slate-100 text-slate-500' : 'bg-orange-100 text-orange-700'}`}>
                                                            {memo.task === 'done' ? 'タスク完了' : 'タスク'}
                                                        </span>
                                                    )}
                                                    {memo.tasklimit instanceof Timestamp && (
                                                        <span className="text-xs text-slate-500">期限: {memo.tasklimit.toDate().toLocaleDateString('ja-JP')}</span>
                                                    )}
                                                    {memo.accountName && (
                                                        <span className="text-xs text-slate-500">担当: {memo.accountName}</span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{memo.text}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* ── Email tab ── */}
                    {activeTab === 'email' && (
                        <div>
                            {emails.length === 0 ? (
                                <div className="text-sm text-slate-400">メールはありません</div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left text-sm">
                                        <thead>
                                            <tr className="border-b border-slate-100">
                                                <th className="pb-2 pr-3 text-xs font-semibold text-slate-400 whitespace-nowrap">種別</th>
                                                <th className="pb-2 pr-3 text-xs font-semibold text-slate-400 whitespace-nowrap">処理状況</th>
                                                <th className="pb-2 pr-1 text-xs font-semibold text-slate-400 whitespace-nowrap">日時</th>
                                                <th className="pb-2 pr-1 text-xs font-semibold text-slate-400 w-[20em]">件名</th>
                                                <th className="pb-2 pr-1 text-xs font-semibold text-slate-400 w-[50em]">本文</th>
                                                <th className="pb-2 text-xs font-semibold text-slate-400 whitespace-nowrap">操作</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {emails.map((email) => {
                                                const dateObj = email.createdAt instanceof Timestamp ? email.createdAt.toDate() : null;
                                                const dateStr = dateObj ? dateObj.toLocaleDateString('ja-JP') : '';
                                                const timeStr = dateObj ? dateObj.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' }) : '';
                                                const isIn = email.direction === 'in';
                                                const isOut = email.direction === 'out';

                                                const directionBadge = isIn
                                                    ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 whitespace-nowrap">受信</span>
                                                    : isOut
                                                    ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 whitespace-nowrap">送信</span>
                                                    : <span className="text-xs text-slate-400">-</span>;

                                                let statusBadge: ReactNode = null;
                                                if (isIn) {
                                                    const s = email.status;
                                                    statusBadge = s === 'done'
                                                        ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 whitespace-nowrap">処理済み</span>
                                                        : s === 'processing'
                                                        ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 whitespace-nowrap">対応中</span>
                                                        : <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-600 whitespace-nowrap">未処理</span>;
                                                } else if (isOut) {
                                                    const ss = email.sendStatus;
                                                    statusBadge = ss === 'sent'
                                                        ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 whitespace-nowrap">送信済み</span>
                                                        : ss === 'draft'
                                                        ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 whitespace-nowrap">下書き</span>
                                                        : ss === 'error'
                                                        ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-600 whitespace-nowrap">送信エラー</span>
                                                        : <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 whitespace-nowrap">送信待ち</span>;
                                                }

                                                const subjectText = (email.subject ?? '-');
                                                const subjectDisplay = subjectText.length > 20 ? subjectText.slice(0, 20) + '…' : subjectText;
                                                const bodyRaw = (email.body ?? '').replace(/\n/g, ' ').trim();
                                                const bodyDisplay = bodyRaw.length > 50 ? bodyRaw.slice(0, 50) + '…' : (bodyRaw || '-');

                                                return (
                                                    <tr key={email.id} className="hover:bg-slate-50">
                                                        <td className="py-2 pr-3 whitespace-nowrap">{directionBadge}</td>
                                                        <td className="py-2 pr-3 whitespace-nowrap">{statusBadge}</td>
                                                        <td className="py-2 pr-1 text-slate-500 text-sm whitespace-nowrap leading-snug text-center">
                                                            <div>{dateStr}</div>
                                                            <div>{timeStr}</div>
                                                        </td>
                                                        <td className="py-2 pr-1 text-slate-700 text-sm w-[20em] whitespace-nowrap">{subjectDisplay}</td>
                                                        <td className="py-2 pr-1 text-slate-400 text-sm w-[50em]">{bodyDisplay}</td>
                                                        <td className="py-2">
                                                            <button
                                                                onClick={() => setSelectedEmail(email)}
                                                                className="flex items-center gap-1 text-xs font-medium text-[#00b074] hover:underline whitespace-nowrap"
                                                            >
                                                                <Eye size={12} />
                                                                確認
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── File tab ── */}
                    {activeTab === 'file' && (
                        <div>
                            {files.length === 0 ? (
                                <div className="text-sm text-slate-400">ファイルはありません</div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left text-sm">
                                        <thead>
                                            <tr className="border-b border-slate-100">
                                                <th className="pb-2 pr-3 text-xs font-semibold text-slate-400 whitespace-nowrap">タイトル</th>
                                                <th className="pb-2 pr-3 text-xs font-semibold text-slate-400 whitespace-nowrap">ファイル名</th>
                                                <th className="pb-2 pr-3 text-xs font-semibold text-slate-400 whitespace-nowrap">アップロード日</th>
                                                <th className="pb-2 pr-3 text-xs font-semibold text-slate-400 whitespace-nowrap">担当者</th>
                                                <th className="pb-2 text-xs font-semibold text-slate-400 whitespace-nowrap">操作</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {files.map((file) => {
                                                const dateStr = file.uploadDate instanceof Timestamp
                                                    ? file.uploadDate.toDate().toLocaleString('ja-JP')
                                                    : '';
                                                return (
                                                    <tr key={file.id} className="hover:bg-slate-50">
                                                        <td className="py-2 pr-3 text-slate-700">{file.title || '-'}</td>
                                                        <td className="py-2 pr-3 text-slate-500">{file.fileName || '-'}</td>
                                                        <td className="py-2 pr-3 text-slate-500 whitespace-nowrap">{dateStr}</td>
                                                        <td className="py-2 pr-3 text-slate-500 whitespace-nowrap">{file.uploadedByName || '-'}</td>
                                                        <td className="py-2">
                                                            <div className="flex items-center gap-3">
                                                                <button
                                                                    onClick={() => handleDownloadFile(file)}
                                                                    className="flex items-center gap-1 text-xs font-medium text-[#00b074] hover:underline whitespace-nowrap"
                                                                >
                                                                    <Download size={12} />
                                                                    ダウンロード
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteFile(file)}
                                                                    className="flex items-center gap-1 text-xs font-medium text-rose-500 hover:underline whitespace-nowrap"
                                                                >
                                                                    <Trash2 size={12} />
                                                                    削除
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Order追加モーダル ─────────────────────────────────────────── */}
            {isAddingOrder && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-5 border-b border-slate-100">
                            <h2 className="text-base font-bold text-slate-800">Order追加</h2>
                            <button onClick={() => { setIsAddingOrder(false); resetOrderForm(); }} className="text-slate-400 hover:text-slate-600">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">ユーザー</label>
                                <input value={userData?.display_name ? `${userData.display_name} (${id})` : id || ''} disabled className={`${inputCls} bg-slate-50 text-slate-400`} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">資格 <span className="text-rose-500">*</span></label>
                                <select value={newOrderForm.shikakuId} onChange={(e) => setNewOrderForm({ ...newOrderForm, shikakuId: e.target.value })} className={`${inputCls} ${inputFocus}`}>
                                    <option value="">選択してください</option>
                                    {shikakuList.map((s) => <option key={s.id} value={s.id}>{s.ShikakuName}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">ステータス</label>
                                <select value={newOrderForm.status} onChange={(e) => setNewOrderForm({ ...newOrderForm, status: e.target.value })} className={`${inputCls} ${inputFocus}`}>
                                    <option value="active">有効</option>
                                    <option value="cancel">キャンセル</option>
                                    <option value="expired">期限切れ</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">コース</label>
                                <select value={newOrderForm.course} onChange={(e) => setNewOrderForm({ ...newOrderForm, course: e.target.value })} className={`${inputCls} ${inputFocus}`}>
                                    <option value="通信講座＋試験">通信講座＋試験</option>
                                    <option value="通信講座のみ">通信講座のみ</option>
                                    <option value="試験のみ">試験のみ</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">認定証</label>
                                <select value={newOrderForm.diploma} onChange={(e) => setNewOrderForm({ ...newOrderForm, diploma: e.target.value })} className={`${inputCls} ${inputFocus}`}>
                                    <option value="通常版">通常版</option>
                                    <option value="英語版">英語版</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">支払方法</label>
                                <select value={newOrderForm.paymentMethod} onChange={(e) => setNewOrderForm({ ...newOrderForm, paymentMethod: e.target.value })} className={`${inputCls} ${inputFocus}`}>
                                    <option value="クレジット">クレジット</option>
                                    <option value="銀行振込">銀行振込</option>
                                    <option value="現金">現金</option>
                                    <option value="無料">無料</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">金額</label>
                                <input type="number" min="0" value={newOrderForm.totalAmount} onChange={(e) => setNewOrderForm({ ...newOrderForm, totalAmount: Number(e.target.value) || 0 })} className={`${inputCls} ${inputFocus}`} />
                            </div>
                            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                                <input type="checkbox" checked={newOrderForm.isExamFinished} onChange={(e) => setNewOrderForm({ ...newOrderForm, isExamFinished: e.target.checked })} className="w-4 h-4 rounded" style={{ accentColor: BRAND }} />
                                受験済みとして登録
                            </label>
                            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-600">
                                注文番号は自動生成。申込日は現在日時、有効期限は2年後に設定されます。
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 p-5 border-t border-slate-100">
                            <button onClick={() => { setIsAddingOrder(false); resetOrderForm(); }} className="px-4 py-2 text-sm border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition">キャンセル</button>
                            <button onClick={handleAddOrder} disabled={addingOrder} className="px-4 py-2 text-sm font-semibold text-white rounded-lg hover:opacity-90 transition disabled:opacity-60" style={{ backgroundColor: BRAND }}>
                                {addingOrder ? '追加中...' : '追加'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── メモ詳細・編集モーダル ───────────────────────────────────────── */}
            {isMemoDetailOpen && selectedMemo && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
                    onClick={() => { if (!memoDetailSaving) { setIsMemoDetailEditing(false); setIsMemoDetailOpen(false); } }}
                >
                    <div
                        className="w-[90%] max-w-[600px] min-w-[320px] max-h-[80vh] rounded-xl border border-gray-300 bg-white shadow-lg flex flex-col overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="bg-gray-600 text-white text-center py-3 text-lg font-semibold">メモ詳細</div>
                        <div className="flex-1 overflow-y-auto">
                            <div className="grid gap-4 border-b border-gray-100 px-5 py-4 md:grid-cols-2">
                                <div>
                                    <div className="text-xs text-gray-400 mb-1">作成日時</div>
                                    <div className="text-sm font-semibold text-gray-900">
                                        {selectedMemo.CreateAt instanceof Timestamp ? selectedMemo.CreateAt.toDate().toLocaleString('ja-JP') : '-'}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-400 mb-1">作成者</div>
                                    <div className="text-sm font-semibold text-gray-900">{selectedMemo.createUserName || '-'}</div>
                                </div>
                            </div>
                            <div className="px-5 py-4 space-y-4">
                                {/* タスクフラグ */}
                                <div className="space-y-2">
                                    <span className="text-sm font-semibold text-gray-700">タスクフラグ</span>
                                    {isMemoDetailEditing ? (
                                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                                            <label className="flex items-center gap-2">
                                                <input type="radio" name="detail-task" checked={memoDetailTask === null}
                                                    onChange={() => { setMemoDetailTask(null); setMemoDetailTaskLimit(''); setMemoDetailAccountId(''); }} />
                                                ーー
                                            </label>
                                            <label className="flex items-center gap-2">
                                                <input type="radio" name="detail-task" checked={memoDetailTask === 'active'}
                                                    onChange={() => setMemoDetailTask('active')} />
                                                タスク
                                            </label>
                                            <label className="flex items-center gap-2">
                                                <input type="radio" name="detail-task" checked={memoDetailTask === 'done'}
                                                    onChange={() => setMemoDetailTask('done')} />
                                                タスク完了
                                            </label>
                                        </div>
                                    ) : (
                                        <div className="text-sm text-gray-700">
                                            {selectedMemo.task === 'active' ? 'タスク' : selectedMemo.task === 'done' ? 'タスク完了' : 'ーー'}
                                        </div>
                                    )}
                                </div>
                                {/* タスク期日・担当者 */}
                                {isMemoDetailEditing && (memoDetailTask === 'active' || memoDetailTask === 'done') && (
                                    <>
                                        <div className="space-y-1">
                                            <label className="text-sm font-semibold text-gray-700">タスク期日</label>
                                            <input type="date"
                                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                                value={memoDetailTaskLimit}
                                                onChange={(e) => setMemoDetailTaskLimit(e.target.value)}
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-sm font-semibold text-gray-700">担当者</label>
                                            <select
                                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                                value={memoDetailAccountId}
                                                onChange={(e) => setMemoDetailAccountId(e.target.value)}
                                            >
                                                <option value="">担当者を選択</option>
                                                {adminUsers.map((u) => <option key={u.id} value={u.id}>{u.displayName}</option>)}
                                            </select>
                                        </div>
                                    </>
                                )}
                                {!isMemoDetailEditing && selectedMemo.task && (
                                    <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                                        {selectedMemo.tasklimit instanceof Timestamp && (
                                            <div>
                                                <div className="text-xs text-gray-400 mb-0.5">タスク期日</div>
                                                <div className="font-semibold text-gray-900">{selectedMemo.tasklimit.toDate().toLocaleDateString('ja-JP')}</div>
                                            </div>
                                        )}
                                        {selectedMemo.accountName && (
                                            <div>
                                                <div className="text-xs text-gray-400 mb-0.5">担当者</div>
                                                <div className="font-semibold text-gray-900">{selectedMemo.accountName}</div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {/* 内容 */}
                                <div className="space-y-1">
                                    <div className="text-xs text-gray-400">内容</div>
                                    {isMemoDetailEditing ? (
                                        <textarea
                                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                            rows={5}
                                            value={memoDetailText}
                                            onChange={(e) => setMemoDetailText(e.target.value)}
                                            placeholder="メモ内容を入力してください"
                                        />
                                    ) : (
                                        <div className="text-sm font-semibold text-gray-900 whitespace-pre-wrap leading-6">{selectedMemo.text || '-'}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="bg-gray-50 px-5 py-4 space-y-3">
                            {isMemoDetailEditing ? (
                                <>
                                    <button type="button" onClick={handleUpdateMemoDetail} disabled={memoDetailSaving || !memoDetailText.trim()}
                                        className="w-full rounded-lg bg-emerald-400 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-300">
                                        {memoDetailSaving ? '更新中...' : 'メモを更新'}
                                    </button>
                                    <button type="button" onClick={handleDeleteMemoDetail} disabled={memoDetailSaving || !canDeleteMemo(selectedMemo)}
                                        className="w-full rounded-lg bg-red-600 py-3 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-300">
                                        {memoDetailSaving ? '処理中...' : 'メモを削除'}
                                    </button>
                                    <button type="button" onClick={() => { setMemoDetailText(selectedMemo.text ?? ''); setIsMemoDetailEditing(false); }}
                                        disabled={memoDetailSaving}
                                        className="w-full rounded-lg bg-gray-600 py-3 text-sm font-semibold text-white hover:opacity-80 disabled:bg-gray-300">
                                        キャンセル
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button type="button" onClick={() => setIsMemoDetailEditing(true)}
                                        className="w-full rounded-lg bg-emerald-400 py-3 text-sm font-semibold text-white hover:opacity-80">
                                        編集
                                    </button>
                                    {!canDeleteMemo(selectedMemo) && (
                                        <div className="text-center text-xs text-gray-500">※削除は作成者のみ可能です</div>
                                    )}
                                </>
                            )}
                            <button type="button" onClick={() => { setIsMemoDetailEditing(false); setIsMemoDetailOpen(false); }}
                                disabled={memoDetailSaving}
                                className="w-full rounded-lg bg-gray-600 py-3 text-sm font-semibold text-white hover:opacity-80 disabled:bg-gray-300">
                                閉じる
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── メモ追加モーダル ──────────────────────────────────────────── */}
            {isMemoModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-lg">
                        <div className="text-center text-lg font-semibold text-gray-800">メモ作成</div>
                        <div className="mt-6 space-y-4">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-500">作成日時 / 作成者</span>
                                <span className="font-medium text-gray-800">
                                    {formatMemoDate(memoCreatedAt)} &nbsp; {currentStaffName || auth.currentUser?.displayName || auth.currentUser?.email || auth.currentUser?.uid || '-'}
                                </span>
                            </div>
                            <div className="space-y-1">
                                <label className="text-sm font-semibold text-gray-700">注文</label>
                                {orders.length === 0 ? (
                                    <div className="text-sm text-gray-400">関連する注文がありません</div>
                                ) : orders.length === 1 ? (
                                    <div className="text-sm font-medium text-gray-800">
                                        {[orders[0].shikakuName, orders[0].orderNumber].filter(Boolean).join(' / ')}
                                    </div>
                                ) : (
                                    <select
                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                        value={memoOrderId}
                                        onChange={(e) => setMemoOrderId(e.target.value)}
                                    >
                                        <option value="">注文を選択（任意）</option>
                                        {orders.map((o) => (
                                            <option key={o.id} value={o.id}>
                                                {[o.shikakuName, o.orderNumber].filter(Boolean).join(' / ')}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                            <div className="space-y-2">
                                <span className="text-sm font-semibold text-gray-700">タスクフラグ</span>
                                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                                    <label className="flex items-center gap-2">
                                        <input type="radio" name="memo-task-flag" checked={memoTaskStatus === null}
                                            onChange={() => { setMemoTaskStatus(null); setMemoTaskLimit(''); setMemoAccountId(''); }} />
                                        ーー
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <input type="radio" name="memo-task-flag" checked={memoTaskStatus === 'active'}
                                            onChange={() => setMemoTaskStatus('active')} />
                                        タスク
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <input type="radio" name="memo-task-flag" checked={memoTaskStatus === 'done'}
                                            onChange={() => setMemoTaskStatus('done')} />
                                        タスク完了
                                    </label>
                                </div>
                            </div>
                            {(memoTaskStatus === 'active' || memoTaskStatus === 'done') && (
                                <>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">タスク期日</label>
                                        <input
                                            type="date"
                                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                            value={memoTaskLimit}
                                            onChange={(e) => setMemoTaskLimit(e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">担当者</label>
                                        <select
                                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                            value={memoAccountId}
                                            onChange={(e) => setMemoAccountId(e.target.value)}
                                        >
                                            <option value="">担当者を選択</option>
                                            {adminUsers.map((u) => (
                                                <option key={u.id} value={u.id}>{u.displayName}</option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            )}
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-gray-700">内容</label>
                                <textarea
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                    rows={4}
                                    value={memoText}
                                    onChange={(e) => setMemoText(e.target.value)}
                                    placeholder="メモ内容を入力してください"
                                    autoFocus
                                />
                            </div>
                        </div>
                        <div className="mt-6 space-y-3">
                            <button
                                type="button"
                                onClick={handleCreateMemo}
                                disabled={addingMemo || !memoText.trim()}
                                className="w-full rounded-lg bg-black py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-300"
                            >
                                {addingMemo ? '保存中...' : 'メモを保存'}
                            </button>
                            <button
                                type="button"
                                className="w-full rounded-lg border border-gray-200 py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                                onClick={() => setIsMemoModalOpen(false)}
                                disabled={addingMemo}
                            >
                                閉じる
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── ファイル追加モーダル ──────────────────────────────────────── */}
            {isFileModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-lg">
                        <div className="text-center text-lg font-semibold text-gray-800">ファイル追加</div>
                        <div className="mt-6 space-y-4">
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-gray-700">タイトル</label>
                                <input
                                    type="text"
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                    value={fileTitle}
                                    onChange={(e) => setFileTitle(e.target.value)}
                                    placeholder="タイトルを入力してください"
                                    autoFocus
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-gray-700">ファイル</label>
                                <input
                                    type="file"
                                    className="w-full text-sm"
                                    onChange={(e) => setSelectedUploadFile(e.target.files?.[0] || null)}
                                />
                            </div>
                        </div>
                        <div className="mt-6 space-y-3">
                            <button
                                type="button"
                                onClick={handleUploadFile}
                                disabled={uploadingFile || !fileTitle.trim() || !selectedUploadFile}
                                className="w-full rounded-lg bg-black py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-300"
                            >
                                {uploadingFile ? 'アップロード中...' : 'アップロード'}
                            </button>
                            <button
                                type="button"
                                className="w-full rounded-lg border border-gray-200 py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                                onClick={() => setIsFileModalOpen(false)}
                                disabled={uploadingFile}
                            >
                                閉じる
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── メール詳細モーダル ────────────────────────────────────────── */}
            {selectedEmail && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-5 border-b border-slate-100">
                            <div className="flex items-center gap-2">
                                <Mail size={16} className="text-[#00b074]" />
                                <h2 className="text-base font-bold text-slate-800">{selectedEmail.subject || '件名なし'}</h2>
                            </div>
                            <button onClick={() => setSelectedEmail(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <div className="flex items-center gap-3 text-xs text-slate-400">
                                {selectedEmail.createdAt instanceof Timestamp && (
                                    <span>{selectedEmail.createdAt.toDate().toLocaleString('ja-JP')}</span>
                                )}
                                {selectedEmail.sendStatus && (
                                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{selectedEmail.sendStatus}</span>
                                )}
                            </div>
                            <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                                {selectedEmail.body || '（本文なし）'}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
